// src/services/chatService.js
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';

import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';

import { db, storage } from '../../firebase';
import { store } from '../../store'; // Import your Redux store
import { tutorApi } from './student/TutorService';

// Collection names
const CHAT_BOXES_COLLECTION = 'chat_boxes';
const MESSAGES_COLLECTION = 'messages';

// Message status constants (updated to 0,1,2)
export const MESSAGE_STATUS = {
  SENT: 0,        // Message sent but not delivered
  DELIVERED: 1,   // Message delivered to recipient
  READ: 2         // Message read by recipient
};

// Media type constants
export const MEDIA_TYPES = {
  NONE: 'NONE',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  AUDIO: 'AUDIO',
  LOCATION: 'LOCATION',
  SEND_PROFILE: 'SEND_PROFILE',
  SEND_LESSON: 'SEND_LESSON',
  SEND_ACHIEVEMENT: 'SEND_ACHIEVEMENT'
};

// Get or create a chat box between two users
export const getOrCreateChatBox = async (user1Id, user2Id, user1Type, user2Type) => {
  try {
    // Sort IDs to ensure consistent chat_box_id
    const sortedIds = [parseInt(user1Id), parseInt(user2Id)].sort((a, b) => a - b);
    const chatBoxId = `${sortedIds[0]}_${sortedIds[1]}`;

    const chatBoxRef = doc(db, CHAT_BOXES_COLLECTION, chatBoxId);
    const chatBoxSnap = await getDoc(chatBoxRef);

    if (chatBoxSnap.exists()) {
      return { id: chatBoxSnap.id, ...chatBoxSnap.data() };
    }

    // Create new chat box - simplified without participant names
    const newChatBox = {
      ids: sortedIds,
      last_message: '',
      last_message_date: serverTimestamp(),
      created_at: serverTimestamp()
    };

    await setDoc(chatBoxRef, newChatBox);
    return { id: chatBoxId, ...newChatBox };
  } catch (error) {
    console.error('Error creating/getting chat box:', error);
    throw error;
  }
};

// Send a message
// Update sendMessage function to better handle media
export const sendMessage = async (
  chatBoxId, 
  senderId, 
  receiverId, 
  message, 
  senderType, 
  mediaType = MEDIA_TYPES.NONE, 
  mediaPath = null, 
  mediaDuration = null,
  mediaData = null // New parameter for additional media data
) => {
  try {
    const messageData = {
      chat_box_id: chatBoxId,
      sender_id: parseInt(senderId),
      receiver_id: parseInt(receiverId),
      sender_type: senderType,
      message: message,
      media_type: mediaType,
      media_path: mediaPath,
      media_duration: mediaDuration,
      media_data: mediaData, // Store additional media data (like thumbnail for videos)
      message_status: MESSAGE_STATUS.DELIVERED,
      created_at: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, MESSAGES_COLLECTION), messageData);

    // Update chat box's last message
    const chatBoxRef = doc(db, CHAT_BOXES_COLLECTION, chatBoxId);
    const lastMessage = mediaType !== MEDIA_TYPES.NONE 
      ? `${mediaType.toLowerCase()} shared` 
      : message;
    
    await updateDoc(chatBoxRef, {
      last_message: lastMessage,
      last_message_date: serverTimestamp()
    });

    return { id: docRef.id, ...messageData };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Listen for messages in a chat box
export const listenToMessages = (chatBoxId, callback) => {
  const messagesRef = collection(db, MESSAGES_COLLECTION);
  const q = query(
    messagesRef,
    where('chat_box_id', '==', chatBoxId),
    orderBy('created_at', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const messages = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    callback(messages);
  });
};

// Get user's chat boxes with participant info (works for both students and tutors)
export const listenToUserChatBoxes = (userId, userType, callback) => {
  const chatBoxesRef = collection(db, CHAT_BOXES_COLLECTION);
  const q = query(
    chatBoxesRef,
    where('ids', 'array-contains', parseInt(userId)),
    orderBy('last_message_date', 'desc')
  );

  return onSnapshot(q, async (snapshot) => {
    const chatBoxes = [];
    const participantIds = new Set();

    // First pass: collect participant IDs and basic chat info
    snapshot.forEach((doc) => {
      const chatBox = { id: doc.id, ...doc.data() };

      // Get the other participant ID
      const otherParticipantId = chatBox.ids.find(id => id !== parseInt(userId));
      chatBox.otherParticipantId = otherParticipantId;
      chatBox.lastMessageTimeFormatted = chatBox.last_message_date?.toDate?.() || new Date();

      chatBoxes.push(chatBox);
      participantIds.add(otherParticipantId);
    });

    // Fetch participant details from API
    if (participantIds.size > 0) {
      try {
        let participants = [];

        if (userType === 'student') {
          const result = await store.dispatch(
            tutorApi.endpoints.getTutorsByIds.initiate(Array.from(participantIds))
          );
          participants = result.data?.data || [];
        } else if (userType === 'tutor') {
          const result = await store.dispatch(
            tutorApi.endpoints.getStudentsByIds.initiate(Array.from(participantIds))
          );
          participants = result.data?.data || [];
        }

        const participantMap = new Map(participants.map(participant => {
          const id = userType === 'student' ? participant.tutorId : participant.id;
          return [id, participant];
        }));

        // Second pass: enrich chat boxes with participant info
        const enrichedChatBoxes = chatBoxes.map(chatBox => {
          const participant = participantMap.get(chatBox.otherParticipantId);
          const participantType = userType === 'student' ? 'tutor' : 'student';

          return {
            ...chatBox,
            otherParticipantName: participant?.name || `${participantType} ${chatBox.otherParticipantId}`,
            otherParticipantType: participantType,
            otherParticipantImage: participant?.imagePath ? `${process.env.REACT_APP_IMAGE_PATH}${participant.imagePath}` : null
          };
        });

        callback(enrichedChatBoxes);
      } catch (error) {
        console.error('Error fetching participant details:', error);
        // Fallback: return chat boxes with basic info
        const participantType = userType === 'student' ? 'tutor' : 'student';
        const fallbackChatBoxes = chatBoxes.map(chatBox => ({
          ...chatBox,
          otherParticipantName: `${participantType} ${chatBox.otherParticipantId}`,
          otherParticipantType: participantType,
          otherParticipantImage: null
        }));
        callback(fallbackChatBoxes);
      }
    } else {
      callback(chatBoxes);
    }
  });
};

// Get available students for tutor to message
export const getAvailableStudentsForTutor = async (tutorId) => {
  try {
    // First, get all chat boxes for the tutor to find already messaged students
    const chatBoxesRef = collection(db, CHAT_BOXES_COLLECTION);
    const q = query(chatBoxesRef, where('ids', 'array-contains', parseInt(tutorId)));
    const querySnapshot = await getDocs(q);

    const messagedStudentIds = new Set();
    querySnapshot.forEach(doc => {
      const chatBox = doc.data();
      const studentId = chatBox.ids.find(id => id !== parseInt(tutorId));
      if (studentId) messagedStudentIds.add(studentId);
    });

    // In a real app, you would fetch all students and filter out the messaged ones
    // For now, return empty since we don't have "get all students" endpoint
    return [];
  } catch (error) {
    console.error('Error getting available students:', error);
    return [];
  }
};

// Get tutors by IDs from API
export const getTutorsByIds = async (tutorIds) => {
  try {
    if (!tutorIds || tutorIds.length === 0) return [];

    // Get token from Redux store
    const state = store.getState();
    const token = state.auth.token;

    if (!token) {
      throw new Error('No authentication token found');
    }

    // Convert to query parameters
    const queryParams = tutorIds.map(id => `ids=${id}`).join('&');
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/WebTutor/GetTutorsByIds?${queryParams}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();

    if (result.succeeded) {
      return result.data || [];
    } else {
      throw new Error(result.errors?.join(', ') || 'Failed to fetch tutors');
    }
  } catch (error) {
    console.error('Error fetching tutors by IDs:', error);
    throw error;
  }
};

// Get all available tutors (for starting new chats)
export const getAllTutors = async () => {
  try {
    // This would be your API endpoint to get all tutors
    // For now, we'll return an empty array since we don't have this endpoint
    // You can implement this based on your available API
    return [];
  } catch (error) {
    console.error('Error fetching all tutors:', error);
    return [];
  }
};

// Get tutors that student hasn't messaged yet
export const getAvailableTutorsForStudent = async (studentId) => {
  try {
    // First, get all chat boxes for the student to find already messaged tutors
    const chatBoxesRef = collection(db, CHAT_BOXES_COLLECTION);
    const q = query(chatBoxesRef, where('ids', 'array-contains', parseInt(studentId)));
    const querySnapshot = await getDocs(q);

    const messagedTutorIds = new Set();
    querySnapshot.forEach(doc => {
      const chatBox = doc.data();
      const tutorId = chatBox.ids.find(id => id !== parseInt(studentId));
      if (tutorId) messagedTutorIds.add(tutorId);
    });

    // In a real app, you would fetch all tutors and filter out the messaged ones
    // For now, return empty since we don't have "get all tutors" endpoint
    return [];
  } catch (error) {
    console.error('Error getting available tutors:', error);
    return [];
  }
};

// Update message status
export const updateMessageStatus = async (messageId, status) => {
  try {
    const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
    await updateDoc(messageRef, {
      message_status: status
    });
  } catch (error) {
    console.error('Error updating message status:', error);
  }
};

// Mark messages as delivered
export const markMessagesAsDelivered = async (chatBoxId, userId) => {
  try {
    const messagesRef = collection(db, MESSAGES_COLLECTION);
    const q = query(
      messagesRef,
      where('chat_box_id', '==', chatBoxId),
      where('receiver_id', '==', parseInt(userId)),
      where('message_status', '==', MESSAGE_STATUS.SENT) // Only update SENT messages to DELIVERED
    );

    const querySnapshot = await getDocs(q);
    const updatePromises = [];

    querySnapshot.forEach((doc) => {
      updatePromises.push(updateDoc(doc.ref, { message_status: MESSAGE_STATUS.DELIVERED }));
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking messages as delivered:', error);
  }
};

// Mark messages as read
export const markMessagesAsRead = async (chatBoxId, userId) => {
  try {
    const messagesRef = collection(db, MESSAGES_COLLECTION);
    const q = query(
      messagesRef,
      where('chat_box_id', '==', chatBoxId),
      where('receiver_id', '==', parseInt(userId)),
      where('message_status', 'in', [MESSAGE_STATUS.SENT, MESSAGE_STATUS.DELIVERED]) // Update both SENT and DELIVERED to READ
    );

    const querySnapshot = await getDocs(q);
    const updatePromises = [];

    querySnapshot.forEach((doc) => {
      updatePromises.push(updateDoc(doc.ref, { message_status: MESSAGE_STATUS.READ }));
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

// Get unread message count per chat box
export const getUnreadMessageCount = (userId, callback) => {
  const messagesRef = collection(db, MESSAGES_COLLECTION);
  const q = query(
    messagesRef,
    where('receiver_id', '==', parseInt(userId)),
    where('message_status', 'in', [MESSAGE_STATUS.SENT, MESSAGE_STATUS.DELIVERED]) // Count both SENT and DELIVERED as unread
  );

  return onSnapshot(q, (snapshot) => {
    const countsByChat = {};
    snapshot.forEach((doc) => {
      const message = doc.data();
      const chatBoxId = message.chat_box_id;
      countsByChat[chatBoxId] = (countsByChat[chatBoxId] || 0) + 1;
    });

    // Also include total count for convenience
    const totalCount = snapshot.size;
    callback({
      ...countsByChat,
      total: totalCount
    });
  });
};

// Helper function to get message status text
export const getMessageStatusText = (status) => {
  switch (status) {
    case MESSAGE_STATUS.SENT:
      return 'Sent';
    case MESSAGE_STATUS.DELIVERED:
      return 'Delivered';
    case MESSAGE_STATUS.READ:
      return 'Read';
    default:
      return 'Unknown';
  }
};

// Helper function to check if message is unread
export const isMessageUnread = (message, currentUserId) => {
  return message.receiver_id === parseInt(currentUserId) && 
         message.message_status < MESSAGE_STATUS.READ;
};

// Upload media file to Firebase Storage
export const uploadMedia = async (file, chatBoxId, userId) => {
  try {
    if (!file || !chatBoxId || !userId) {
      throw new Error('Missing required parameters for media upload');
    }

    // Determine media type based on file type
    let mediaType = MEDIA_TYPES.NONE;
    const fileType = file.type.split('/')[0].toUpperCase();
    
    switch (fileType) {
      case 'IMAGE':
        mediaType = MEDIA_TYPES.IMAGE;
        break;
      case 'VIDEO':
        mediaType = MEDIA_TYPES.VIDEO;
        break;
      case 'AUDIO':
        mediaType = MEDIA_TYPES.AUDIO;
        break;
      default:
        mediaType = MEDIA_TYPES.NONE;
    }

    // Generate unique filename
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${chatBoxId}_${userId}_${timestamp}.${fileExtension}`;
    
    // Create storage reference
    const storageRef = ref(storage, `chat_media/${chatBoxId}/${fileName}`);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    // For videos and audio, get duration (you'll need to implement this)
    let mediaDuration = null;
    if (mediaType === MEDIA_TYPES.VIDEO || mediaType === MEDIA_TYPES.AUDIO) {
      mediaDuration = await getMediaDuration(file);
    }

    return {
      url: downloadURL,
      type: mediaType,
      duration: mediaDuration,
      fileName: fileName
    };

  } catch (error) {
    console.error('Error uploading media:', error);
    throw error;
  }
};

// Get media duration for audio/video files
const getMediaDuration = (file) => {
  return new Promise((resolve) => {
    if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      const url = URL.createObjectURL(file);
      const media = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
      
      media.onloadedmetadata = () => {
        resolve(Math.round(media.duration));
        URL.revokeObjectURL(url);
      };
      
      media.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(url);
      };
      
      media.src = url;
      media.load();
    } else {
      resolve(null);
    }
  });
};

// Delete media from storage
export const deleteMedia = async (filePath) => {
  try {
    if (!filePath) return;
    
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting media:', error);
    throw error;
  }
};

// Helper function to get media preview
export const getMediaPreview = (media) => {
  if (!media || !media.type || !media.url) return null;
  
  switch (media.type) {
    case MEDIA_TYPES.IMAGE:
      return {
        type: 'image',
        url: media.url,
        thumbnail: media.thumbnail || media.url
      };
    case MEDIA_TYPES.VIDEO:
      return {
        type: 'video',
        url: media.url,
        thumbnail: media.thumbnail || null,
        duration: media.duration
      };
    case MEDIA_TYPES.AUDIO:
      return {
        type: 'audio',
        url: media.url,
        duration: media.duration
      };
    default:
      return null;
  }
};